import { useEffect } from 'preact/hooks';
import { navStack, authChecked, bootstrap, quickAddOpen, openQuickAdd } from './store.js';
import BottomNav from './components/BottomNav.jsx';
import Toast from './components/Toast.jsx';
import UpcomingPanel from './components/UpcomingPanel.jsx';
import QuickAddFab from './components/QuickAddFab.jsx';
import QuickAddSheet from './components/QuickAddSheet.jsx';
import { __ } from './i18n.js';

import AuthScreen from './screens/AuthScreen.jsx';
import ResetPasswordScreen from './screens/ResetPasswordScreen.jsx';
import VandaagScreen from './screens/VandaagScreen.jsx';
import WeekScreen from './screens/WeekScreen.jsx';
import ScheduleFormScreen from './screens/ScheduleFormScreen.jsx';
import ListsScreen from './screens/ListsScreen.jsx';
import ListDetailScreen from './screens/ListDetailScreen.jsx';
import ShoppingScreen from './screens/ShoppingScreen.jsx';
import CategoryScreen from './screens/CategoryScreen.jsx';
import TimeScreen from './screens/TimeScreen.jsx';
import TaskScreen from './screens/TaskScreen.jsx';
import SkipScreen from './screens/SkipScreen.jsx';
import InterventionScreen from './screens/InterventionScreen.jsx';
import AssignHelpScreen from './screens/AssignHelpScreen.jsx';
import ManageScreen from './screens/ManageScreen.jsx';
import ProjectScreen from './screens/ProjectScreen.jsx';
import ProjectFormScreen from './screens/ProjectFormScreen.jsx';
import TaskFormScreen from './screens/TaskFormScreen.jsx';
import StatsScreen from './screens/StatsScreen.jsx';
import SubscriptionsScreen from './screens/SubscriptionsScreen.jsx';
import SubscriptionFormScreen from './screens/SubscriptionFormScreen.jsx';
import HouseholdScreen from './screens/HouseholdScreen.jsx';
import SettingsScreen from './screens/SettingsScreen.jsx';

// Screen registry — every entry in navStack.name must exist here.
// Each screen component receives a single `params` prop (the current
// nav-stack frame's params) and reads/writes global signals from store.js
// for anything other screens also need to see. Screens navigate via
// navigate()/replace()/goBack()/resetTo() from store.js — never touch
// navStack directly.
const SCREENS = {
  auth: AuthScreen,
  resetPassword: ResetPasswordScreen,
  // HH-1: the 'home' key is deliberately unchanged even though the screen
  // is now VandaagScreen — every existing navigate('home')/resetTo('home')
  // call site (and the Phase 5A in-progress-task resume) keeps working
  // untouched, which is acceptance criterion 1's "byte-for-byte".
  home: VandaagScreen,
  week: WeekScreen,
  // HH-6: the Weekritme item form. Like taskForm/subscriptionForm it is a
  // focused flow, so it stays out of NAV_VISIBLE below.
  scheduleForm: ScheduleFormScreen,
  lists: ListsScreen,
  listDetail: ListDetailScreen,
  // HH-5: winkelmodus — deliberately NOT in NAV_VISIBLE (full-screen, no
  // nav chrome per plan §4.2) and not in FAB_VISIBLE either; the only way
  // out is its own close button (goBack to the list detail).
  shopping: ShoppingScreen,
  category: CategoryScreen,
  time: TimeScreen,
  task: TaskScreen,
  skip: SkipScreen,
  intervention: InterventionScreen,
  assignHelp: AssignHelpScreen,
  manage: ManageScreen,
  // HH-8: task-adjacent, grouped under the Taken tab (plan §3) rather than
  // earning their own bottom-nav entry — the MD3 five-tab ceiling HH-1
  // already locked in. `project` is a browse/detail screen like
  // listDetail (keeps the nav chrome); `projectForm` is a focused edit
  // flow like taskForm/subscriptionForm (doesn't).
  project: ProjectScreen,
  projectForm: ProjectFormScreen,
  taskForm: TaskFormScreen,
  stats: StatsScreen,
  subscriptions: SubscriptionsScreen,
  subscriptionForm: SubscriptionFormScreen,
  household: HouseholdScreen,
  settings: SettingsScreen,
};

// Screens that show the bottom tab bar. Focused/modal-like flows (skip
// reason, task add/edit form) and the auth/loading screens omit it, same
// as the v1 app's screen layout.
const NAV_VISIBLE = new Set(['home', 'week', 'lists', 'listDetail', 'category', 'time', 'task', 'manage', 'project', 'settings', 'stats', 'subscriptions', 'household']);

// WP-6: the FAB shows only on these three screens (brief: "home, category
// and manage"), a narrower set than NAV_VISIBLE — the desktop keyboard
// shortcut below deliberately reaches the wider "any main screen" set
// instead, per the brief's own distinction between the two affordances.
const FAB_VISIBLE = new Set(['home', 'category', 'manage']);

const QUICK_ADD_DESKTOP_QUERY = '(min-width: 1024px)';

export default function App() {
  useEffect(() => {
    bootstrap();
  }, []);

  // WP-6: desktop keyboard shortcut ("n" opens quick-add from any main
  // screen). Reads navStack/quickAddOpen fresh on every keypress (signals,
  // not stale closures) rather than depending on them, so the listener
  // only needs to attach once. Ignored while the sheet is already open (so
  // typing "n" inside it doesn't re-trigger anything) and while focus is
  // in any text input/textarea/contenteditable (so typing "n" as part of
  // a title elsewhere never fires it either).
  useEffect(() => {
    function onKeyDown(e) {
      if (e.key !== 'n' || e.metaKey || e.ctrlKey || e.altKey) return;
      if (quickAddOpen.value) return;
      if (!window.matchMedia(QUICK_ADD_DESKTOP_QUERY).matches) return;
      const active = document.activeElement;
      const isTyping = active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable);
      if (isTyping) return;
      const frame = navStack.value[navStack.value.length - 1];
      if (!NAV_VISIBLE.has(frame.name)) return;
      e.preventDefault();
      openQuickAdd();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  if (!authChecked.value) {
    return (
      <div class="etm-app">
        <div class="etm-loading-screen">{__('Laden…', 'etm')}</div>
      </div>
    );
  }

  const frame = navStack.value[navStack.value.length - 1];
  const Screen = SCREENS[frame.name] || AuthScreen;

  const appContent = (
    <div class="etm-app">
      <div class={`etm-screen${NAV_VISIBLE.has(frame.name) ? '' : ' etm-screen--no-nav'}`}>
        {/* Keyed per nav frame so screen changes remount the content and
            play the MD3E enter transition (WP-1). Screens already remount
            on every navigation (each frame renders a different component
            type), so the key changes nothing about lifecycle behavior —
            and the scroll container itself stays unkeyed, keeping scroll
            handling exactly as before. */}
        <div class="etm-screen-body" key={`${navStack.value.length}-${frame.name}`}>
          <Screen params={frame.params} />
        </div>
      </div>
      {FAB_VISIBLE.has(frame.name) ? <QuickAddFab /> : null}
      {NAV_VISIBLE.has(frame.name) ? <BottomNav /> : null}
      <Toast />
      {quickAddOpen.value ? <QuickAddSheet /> : null}
    </div>
  );

  // WP-2: the auth-family screens (logged out, no session/tasks yet) keep
  // their existing narrow centered layout unconditionally — a two-pane
  // split next to an empty "Aankomend" sidebar would have nothing
  // meaningful to show, and on the reset screen the panel would even fire
  // an unauthorized /tasks fetch. Every other screen gets the desktop
  // two-pane shell; below the 1024px breakpoint .etm-shell/.etm-pane-left
  // are display:contents (see theme.css), so this wrapping changes
  // nothing about mobile rendering.
  if (frame.name === 'auth' || frame.name === 'resetPassword') {
    return appContent;
  }

  return (
    <div class="etm-shell">
      <div class="etm-pane-left">{appContent}</div>
      <UpcomingPanel />
    </div>
  );
}
